/****
 * Program settings stage maintenance
 * - Preview of the stage experience for a reseller
 * - Maintenance of assets on this stage
 */
import React, { Component } from 'react';
import { QueryRenderer } from 'react-relay';
import graphql from 'babel-plugin-relay/macro';
import { Redirect } from 'react-router-dom';
import _ from 'lodash';

import AbEnvironment from '../auth/AbEnvironment';
import PropTypes from 'prop-types';
import VendorPartnerProgram from './VendorPartnerProgram';
import VppAssetCreateMutation from './VppAssetCreateMutation';
import VppAssetDeleteMutation from './VppAssetDeleteMutation';
import VppAssetUpdateMutation from './VppAssetUpdateMutation';
import VppUpdateMutation from './VppUpdateMutation';
import { getValueForRender } from '../../utils/InputUtils';

import withStyles from '@mui/styles/withStyles';
import { Table, TableHead, TableCell, TableBody, TableRow, TableSortLabel, TextField, Typography } from '@mui/material';
import { Button, Grid, IconButton, MenuItem, Paper, Select } from '@mui/material';
import Add from '@mui/icons-material/Add';
import Delete from '@mui/icons-material/Delete';
 
import ServerError from '../ui/ServerError';
import LoadingSpinner from '../ui/LoadingSpinner';
// import SearchField from '../ui/SearchField';
import AppBindStyles from '../../AppBindStyles';

const abEnvironment = new AbEnvironment();

const ProgramSettingsStageQuery = graphql`
query ProgramSettingsStageQuery($vppId: UUID!, $stage:Int!, $orderBy: [VppAssetTsOrderBy!]) {
  vppTByVppId(vppId:$vppId) {
    ctaTitle
    ctaText,
    ctaButton,
    ctaLabel,
    ctaTitleSuspend,
    ctaTextSuspend,
    ctaTitleDecline,
    ctaTextDecline,
    ctaTitleLeft,
  	ctaTextLeft
    vppAssetTsByVppId(condition:{stage:$stage} orderBy:$orderBy) {
      totalCount
      nodes {
        vppAssetId
        assetType
        assetTitle
        assetOrder
        assetLink
      }
    }
  }
}
`;

const styleSheet = theme => ({
  root: {
    flexGrow: 1,
    //marginTop: 10,
    justifyContent: 'flex-end',
    backgroundColor: theme.palette.background.default,
  },
  flex: {
    flex: 1,
  },
  paper: {
    padding: theme.spacing(2),
    maxWidth: 1200,
  },
  radio: {
    marginTop: 16
  },
  ctaTitle: {
    width: 240
  },
  ctaText: {
    width: 400
  },
  ctaButton: {
    width: 96
  },
  ctaLabel: {
    width: 400
  },
  assetType: {
    width: 72
  },
  assetOrder: {
    width: 32
  },
  assetOrderField: {
    width: 32
  },
  assetTitle: {
    width: 240
  },
  assetAction: {
    width: 40
  }
});

class ProgramSettingsStage extends Component {
  state = {
    assetTypeDir: 0,
    assetOrderDir: 0,
    changedAssets: [],
    refetch: 0,
    ctaTitle: null,
    ctaText: null,
    ctaButton: null,
    ctaLabel: null,
    ctaTitleSuspend: null,
    ctaTextSuspend: null,
    ctaTitleDecline: null,
    ctaTextDecline: null,
    ctaTitleLeft: null,
    ctaTextLeft: null
  };

  idCtr = 0;

  handleChange = name => event => {
    this.setState({
      [name]: event.target.value
    });
  };
 
  toggleTypeOrder = () => {
    let newDir = 0;
    if (this.state.assetTypeDir === 0) {
      newDir = 1;
    }

    this.setState({assetTypeDir: newDir});
  } 
 
  toggleAssetOrder = () => {
    let newDir = 0;
    if (this.state.assetOrderDir === 0) {
      newDir = 1;
    }

    this.setState({assetOrderDir: newDir});
  }

  setAssetValue(id, field, value, assets) {
    let newArray = [];
    let matchFound = false;
    let removeEntry = false;

    for (let i=0; i<assets.length; i++) {
      let theNode = assets[i];
      if (theNode.vppAssetId === id) {
        // Changing a current value
        let changedNode = {
          vppAssetId: theNode.vppAssetId,
          assetType: theNode.assetType,
          assetTitle: theNode.assetTitle,
          assetOrder: theNode.assetOrder,
          assetLink: theNode.assetLink,
          markedForDelete: false
        };

        if (field === 'assetType') {
          changedNode.assetType = value;
        } else if (field === 'assetTitle') {
          changedNode.assetTitle = value;
        } else if (field === 'assetOrder') {
          // Allow empty, but we won't let it save if empty
          changedNode.assetOrder = value;
          /*
          try {
            let aNum = Number.parseInt(value);

            changedNode.assetOrder = aNum;
          } catch (anErr) {
            // swallow
          }
          */
        } else if (field === 'assetLink') {
          changedNode.assetLink = value;
        } else if (field === 'markForDelete') {
          if (id.startsWith('new-')) {
            // Omit this from the array
            removeEntry = true;
          } else {
            changedNode.markedForDelete = true;
          }
        }
        matchFound = true;
        if (removeEntry === false) {
          newArray.push(changedNode);
        } else {
          removeEntry = false; // Reset for next time
        }
      } else {
        if (theNode.markedForDelete == null) {
          let augmentedNode = {
            vppAssetId: theNode.vppAssetId,
            assetType: theNode.assetType,
            assetTitle: theNode.assetTitle,
            assetOrder: theNode.assetOrder,
            assetLink: theNode.assetLink,
            markedForDelete: false
          }
          newArray.push(augmentedNode);
        } else {
          newArray.push(theNode);
        }
      }
    }

    if (matchFound === false) {
      this.idCtr++;
      let anId = 'new-' + this.idCtr;
      let newNode = {
        vppAssetId: anId,
        assetType: 0,
        assetTitle: "",
        assetOrder: 0,
        assetLink: "",
        markedForDelete: false
      };
      if (field === 'assetType') {
        newNode.assetType = value;
      } else if (field === 'assetTitle') {
        newNode.assetTitle = value;
      } else if (field === 'assetOrder') {
        newNode.assetOrder = value;
      } else if (field === 'assetLink') {
        newNode.assetLink = value;
      }

      // Insert at the front of the array
      newArray.unshift(newNode);
    }

    this.setState({changedAssets: newArray});
  }

  compareNodesById = (a,b) => {
    if (a.vppAssetId === b.vppAssetId) {
      return true;
    }
    return false;
  }

  compareNodesFull = (a,b) => {
    if (a.vppAssetId === b.vppAssetId && a.assetType === b.assetType && a.assetTitle === b.assetTitle && 
        Number(a.assetOrder) === Number(b.assetOrder) && a.assetLink === b.assetLink && a.markedForDelete === false) {
      return true;
    }
    return false;
  }

  isSaveDisabled = (propsVpp) => {

    // Only save if there is a new row, a change in a row, or a deletion
    // Also check any block level fields.
    if ( (this.state.ctaTitle        != null && this.state.ctaTitle        !== propsVpp.ctaTitle) ||
         (this.state.ctaText         != null && this.state.ctaText         !== propsVpp.ctaText)  ||
         (this.state.ctaButton       != null && this.state.ctaButton       !== propsVpp.ctaButton) ||
         (this.state.ctaLabel        != null && this.state.ctaLabel        !== propsVpp.ctaLabel) ||
         (this.state.ctaTitleSuspend != null && this.state.ctaTitleSuspend !== propsVpp.ctaTitleSuspend) ||
         (this.state.ctaTextSuspend  != null && this.state.ctaTextSuspend  !== propsVpp.ctaTextSuspend) ||
         (this.state.ctaTitleDecline != null && this.state.ctaTitleDecline !== propsVpp.ctaTitleDecline) ||
         (this.state.ctaTextDecline  != null && this.state.ctaTextDecline  !== propsVpp.ctaTextDecline) ||
         (this.state.ctaTitleLeft    != null && this.state.ctaTitleLeft    !== propsVpp.ctaTitleLeft) ||
         (this.state.ctaTextLeft     != null && this.state.ctaTextLeft     !== propsVpp.ctaTextLeft) )
    {
      return false;
    }

    if (this.state.changedAssets.length === 0) {
      return true;
    }

    // Need a more detailed comparison
    let propsAssets = propsVpp.vppAssetTsByVppId.nodes;
    let diffArray = _.differenceWith(this.state.changedAssets, propsAssets, this.compareNodesFull);
    if (diffArray.length > 0) {
      // Disallow if there are any non-int values that need to be ints
      for (let i=0; i<diffArray.length; i++) {
        let aNode = diffArray[i];
        if (aNode.assetOrder.length === 0) {
          return true;
        }

        try {
          let aNum = Number.parseInt(aNode.assetOrder);
          if (isNaN(aNum)) {
            return true;
          }
        } catch (anErr) {
          return true;
        }

        if (aNode.assetTitle.length === 0 || aNode.assetLink.length === 0) {
          return true;
        }
      }

      return false;
    }

    return true;
  }

  saveChanges = (propsVpp) => {

    let blockChange = false;
    if ( (this.state.ctaTitle        != null && this.state.ctaTitle        !== propsVpp.ctaTitle) ||
         (this.state.ctaText         != null && this.state.ctaText         !== propsVpp.ctaText)  ||
         (this.state.ctaButton       != null && this.state.ctaButton       !== propsVpp.ctaButton) ||
         (this.state.ctaLabel        != null && this.state.ctaLabel        !== propsVpp.ctaLabel) ||
         (this.state.ctaTitleSuspend != null && this.state.ctaTitleSuspend !== propsVpp.ctaTitleSuspend) ||
         (this.state.ctaTextSuspend  != null && this.state.ctaTextSuspend  !== propsVpp.ctaTextSuspend) ||
         (this.state.ctaTitleDecline != null && this.state.ctaTitleDecline !== propsVpp.ctaTitleDecline) ||
         (this.state.ctaTextDecline  != null && this.state.ctaTextDecline  !== propsVpp.ctaTextDecline) ||
         (this.state.ctaTitleLeft    != null && this.state.ctaTitleLeft    !== propsVpp.ctaTitleLeft) ||
         (this.state.ctaTextLeft     != null && this.state.ctaTextLeft     !== propsVpp.ctaTextLeft) )
    {
      blockChange = true;
    }

    let assetsChange = false;
    let diffArray = null;
    if (this.state.changedAssets.length > 0) {
      // Need a more detailed comparison
      let propsAssets = propsVpp.vppAssetTsByVppId.nodes;
      diffArray = _.differenceWith(this.state.changedAssets, propsAssets, this.compareNodesFull);
      if (diffArray.length > 0) {
        assetsChange = true;
      }
    }

    if (blockChange === false && assetsChange === false) {
      return;
    }

    // Do the save
    let deletePromiseArray = [];
    let updatePromiseArray = [];

    Promise.resolve()
    .then(() => {
      if (blockChange === false) {
        return;
      }

      // Update vpp block information
      return VppUpdateMutation.commit(this.props.vppId, null, null, null, null, null, null, null, null, null, null,
        this.state.ctaTitle, this.state.ctaText, this.state.ctaButton, this.state.ctaLabel, this.state.ctaTitleSuspend, this.state.ctaTextSuspend, 
        this.state.ctaTitleDecline, this.state.ctaTextDecline, this.state.ctaTitleLeft, this.state.ctaTextLeft);
    })
    .then(() => {
      if (diffArray == null) {
        return;
      }

      for (let i=0; i<diffArray.length; i++) {
        if (diffArray[i].markedForDelete === true) {
          let deletePromise = VppAssetDeleteMutation.commit(diffArray[i].vppAssetId);
          deletePromiseArray.push(deletePromise);
        }
      }

      if (deletePromiseArray.length === 0) {
        return;
      } else {
        return deletePromiseArray.reduce((promiseChain, currentPromise) => {
          return promiseChain.then(chainResults => 
            currentPromise.then(currentResult => 
              [ ...chainResults, currentResult ]
            )
          );
        }, Promise.resolve([])).then(resultsArray => {
          return resultsArray;
        })
      }
  
    })
    .then(dresp => {
      if (diffArray == null) {
        return;
      }

      for (let i=0; i<diffArray.length; i++) {
        let theNode = diffArray[i];
        if (theNode.markedForDelete !== true) {
          let theOrder = 9;
          try {
            theOrder = Number.parseInt(theNode.assetOrder);
            if (isNaN(theOrder)) {
              theOrder = 9;
            }
          } catch (anErr) {
            theOrder = 9;
          }
         if (theNode.vppAssetId.startsWith('new-')) {
            let createPromise = VppAssetCreateMutation.commit(this.props.vppId, this.props.stage, theNode.assetType, theOrder, theNode.assetTitle, theNode.assetLink);
            updatePromiseArray.push(createPromise);
          } else {
           let updatePromise = VppAssetUpdateMutation.commit(theNode.vppAssetId, theNode.assetType, theOrder, theNode.assetTitle, theNode.assetLink);
            updatePromiseArray.push(updatePromise);
          }
        }
      }

      if (updatePromiseArray.length === 0) {
        return;
      } else {
        // Sequentially execute the update mutations
        return updatePromiseArray.reduce((promiseChain, currentPromise) => {
          return promiseChain.then(chainResults => 
            currentPromise.then(currentResult => 
              [ ...chainResults, currentResult ]
            )
          );
        }, Promise.resolve([])).then(resultsArray => {
          return resultsArray;
        })
      }
    })
    .then(uresp => {
      // Just reload to refresh information
      let refetch = this.state.refetch + 1;
      this.setState({refetch:refetch, changedAssets: []});
    })
    .catch(err => {
      // swallow for now
      console.log('Oh jeez: ' + JSON.stringify(err));
      console.log('ok');
    });
    return;
  }

  addAsset = (assets) => {
    // Push a new node into the array
    this.setAssetValue(null, null, null, assets);
  }

  deleteAsset = (id, assets) => {
    // If this is a new row, make it go away.
    // If this is an existing row, mark it for deletion
    this.setAssetValue(id, 'markForDelete', null, assets);
  }

  render() {
    const { isAuthenticatedVendor, logError } = this.props.auth;
    const classes = this.props.classes;
 
    if (isAuthenticatedVendor()) {
      let vppId = this.props.vppId;
      let stage = this.props.stage;
 
      let theOrderBy = [];

      let typeDir = 'asc';
      if (this.state.assetTypeDir === 0) {
        theOrderBy.push("ASSET_TYPE_ASC");
        typeDir = 'asc';
      } else {
        theOrderBy.push("ASSET_TYPE_DESC");
        typeDir = 'desc';
      }

      theOrderBy.push("ASSET_ORDER_ASC");
      /*
      let assetDir = 'asc';
      if (this.state.assetOrderDir === 0) {
        theOrderBy.push("ASSET_ORDER_ASC");
        assetDir = 'asc';
      } else {
        theOrderBy.push("ASSET_ORDER_DESC");
        assetDir = 'desc';
      }
      */

      let previewText;
      switch(stage) {
        case 1:
          previewText = 'View the partner page seen when a partner has applied';
          break;
        case 2:
          previewText = 'View the partner page seen by active partners';
          break;
        default:
          previewText = 'View the partner page seen by new partners';
          break;
      }

      return (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Grid container spacing={0} direction="row" justifyContent="flex-start">
              <Grid item xs={12}>
                <Typography align="left" variant="h6">Preview</Typography>
              </Grid>                          
              <Grid item xs={12}>
                <Typography variant="body2">{previewText}</Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Paper className={classes.paper}>
              <VendorPartnerProgram
                mode='dev'
                vppId={vppId}
                stage={stage}
                auth={this.props.auth}
                history={this.props.history}
                refetch={this.state.refetch}/>
            </Paper>
          </Grid>
          <Grid item xs={12}>
            <Grid container spacing={0} direction="row" justifyContent="flex-start">
              <Grid item xs={12}>
                <Typography align="left" variant="h6">Manage assets</Typography>
              </Grid>                          
              <Grid item xs={12}>
                <Typography variant="body2">Add and manage assets shown on this page</Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Paper className={classes.paper}>
              <QueryRenderer
                environment={abEnvironment.getEnvironment()}
                query={ProgramSettingsStageQuery}
                variables={{vppId: vppId,
                            stage: stage,
                            orderBy: theOrderBy,
                            forceFetch: this.state.refetch}}
                render={({error, props})=> {
                  if (error) {
                    logError(error.message);
                    return (
                      <ServerError/>
                    );
                  } else if (props) {
                    // Use the combination of props and state for the asset list
                    let stateAssets = this.state.changedAssets;
                    let propsVpp = props.vppTByVppId;
                    let propsAssets = propsVpp.vppAssetTsByVppId.nodes;
                    let assets = _.unionWith(stateAssets, propsAssets, this.compareNodesById);

                    return ( 
                      <Grid container spacing={2}>                        
                        <Grid item xs={12}>
                          {stage === 0 &&
                          <Grid container spacing={2}>
                            <Grid item>
                              <TextField
                                label="Title"
                                id="ctaTitle"
                                className={classes.ctaTitle}
                                value={getValueForRender(this.state.ctaTitle, propsVpp.ctaTitle, '')}
                                onChange={(e) => this.setState({ctaTitle: e.target.value})}
                                inputProps={{ maxLength: 128 }}
                              />
                            </Grid>
                            <Grid item>
                              <TextField
                                label="Text"
                                id="ctaText"
                                multiline
                                className={classes.ctaText}
                                value={getValueForRender(this.state.ctaText, propsVpp.ctaText, '')}
                                onChange={(e) => this.setState({ctaText: e.target.value})}
                                inputProps={{ maxLength: 2048 }}
                              />
                            </Grid>
                            <Grid item>
                              <TextField
                                label="Apply button"
                                id="ctaButton"
                                className={classes.ctaButton}
                                value={getValueForRender(this.state.ctaButton, propsVpp.ctaButton, '')}
                                onChange={(e) => this.setState({ctaButton: e.target.value})}
                                inputProps={{ maxLength: 64 }}
                              />
                            </Grid>
                          </Grid>}
                          {stage === 1 &&
                          <Grid container spacing={2}>
                            <Grid item>
                              <TextField
                                label="Applied"
                                id="ctaLabel"
                                multiline
                                className={classes.ctaLabel}
                                value={getValueForRender(this.state.ctaLabel, propsVpp.ctaLabel, '')}
                                onChange={(e) => this.setState({ctaLabel: e.target.value})}
                                inputProps={{ maxLength: 1024 }}
                              />
                            </Grid>
                          </Grid>}
                          {stage === 3 &&
                          <Grid container spacing={2}>
                            <Grid item>
                              <TextField
                                label="Title"
                                id="ctaTitle"
                                className={classes.ctaTitle}
                                value={getValueForRender(this.state.ctaTitleSuspend, propsVpp.ctaTitleSuspend, '')}
                                onChange={(e) => this.setState({ctaTitleSuspend: e.target.value})}
                                inputProps={{ maxLength: 128 }}
                              />
                            </Grid>
                            <Grid item>
                              <TextField
                                label="Text"
                                id="ctaText"
                                multiline
                                className={classes.ctaText}
                                value={getValueForRender(this.state.ctaTextSuspend, propsVpp.ctaTextSuspend, '')}
                                onChange={(e) => this.setState({ctaTextSuspend: e.target.value})}
                                inputProps={{ maxLength: 2048 }}
                              />
                            </Grid>
                          </Grid>}
                          {stage === 4 &&
                          <Grid container spacing={2}>
                            <Grid item>
                              <TextField
                                label="Title"
                                id="ctaTitle"
                                className={classes.ctaTitle}
                                value={getValueForRender(this.state.ctaTitleDecline, propsVpp.ctaTitleDecline, '')}
                                onChange={(e) => this.setState({ctaTitleDecline: e.target.value})}
                                inputProps={{ maxLength: 128 }}
                              />
                            </Grid>
                            <Grid item>
                              <TextField
                                label="Text"
                                id="ctaText"
                                multiline
                                className={classes.ctaText}
                                value={getValueForRender(this.state.ctaTextDecline, propsVpp.ctaTextDecline, '')}
                                onChange={(e) => this.setState({ctaTextDecline: e.target.value})}
                                inputProps={{ maxLength: 2048 }}
                              />
                            </Grid>
                          </Grid>}
                        </Grid>
                        <Grid item xs={12}>
                          <Table>
                            <TableHead style={AppBindStyles.tableHead}>
                              <TableRow style={AppBindStyles.tableHeadRow}>
                                <TableCell>
                                  <TableSortLabel
                                    active={true}
                                    direction={typeDir}
                                    onClick={() => this.toggleTypeOrder() }>
                                    Type
                                  </TableSortLabel>
                                </TableCell>
                                <TableCell>Order</TableCell>
                                {/* 
                                <TableCell>
                                  <TableSortLabel
                                    active={true}
                                    direction={assetDir}
                                    onClick={() => this.toggleAssetOrder() }>
                                    Order
                                  </TableSortLabel>
                                </TableCell>
                                */}  
                                <TableCell>Title</TableCell>
                                <TableCell>Content</TableCell>
                                <TableCell>
                                  <IconButton
                                    onClick={() => {this.addAsset(assets);}}>
                                    <Add/>
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {assets.map(n => {
                                if (n.markedForDelete === true) {
                                  return null;
                                }
                                /*
                                let theType;
                                switch(n.assetType) {
                                  case 0:
                                    theType = "0. Support";
                                    break;
                                  case 1:
                                    theType = "1. Partner";
                                    break;
                                  case 2:
                                    theType = "2. Future use";
                                    break;
                                  case 3:
                                    theType = "3. Video link";
                                    break;
                                  case 4:
                                    theType = "4. Image link";
                                    break;
                                  default:
                                    theType = "Inactive"
                                    break;   
                                }
                                */

                                let ayId = 'type-' + n.vppAssetId;
                                let aoId = 'order-' + n.vppAssetId;
                                let atId = 'title-' + n.vppAssetId;
                                let alId = 'link-' + n.vppAssetId;
                                
                                return (
                                  <TableRow key={n.vppAssetId}>
                                    <TableCell align="left" className={classes.assetType}>
                                      <Select
                                        value={n.assetType}
                                        className={classes.assetTypeField}
                                        onChange={(e) => this.setAssetValue(n.vppAssetId, 'assetType', e.target.value, assets)}
                                        inputProps={{
                                          name: ayId,
                                          id: ayId,
                                        }}
                                      >
                                        <MenuItem value={0}>0. Support link</MenuItem>
                                        <MenuItem value={1}>1. Partner link</MenuItem>
                                        <MenuItem value={3}>3. Video link</MenuItem>
                                        <MenuItem value={4}>4. Image link</MenuItem>
                                      </Select>
                                    </TableCell>
                                    <TableCell align="left" className={classes.assetOrder}>
                                      <TextField
                                        id={aoId}
                                        className={classes.assetOrderField}
                                        value={n.assetOrder}
                                        type="number"
                                        onChange={(e) => this.setAssetValue(n.vppAssetId, 'assetOrder', e.target.value, assets)}
                                        inputProps={{ maxLength: 3 }}
                                      />
                                    </TableCell>
                                    <TableCell align="left" className={classes.assetTitle}>
                                      <TextField
                                        id={atId}
                                        value={n.assetTitle}
                                        onChange={(e) => this.setAssetValue(n.vppAssetId, 'assetTitle', e.target.value, assets)}
                                        inputProps={{ maxLength: 64 }}
                                      />
                                    </TableCell>
                                    <TableCell align="left">
                                      <TextField
                                        id={alId}
                                        value={n.assetLink}
                                        onChange={(e) => this.setAssetValue(n.vppAssetId, 'assetLink', e.target.value, assets)}
                                        inputProps={{ maxLength: 256 }}
                                      />
                                    </TableCell>
                                    <TableCell className={classes.assetAction}>
                                      <IconButton
                                        onClick={() => {this.deleteAsset(n.vppAssetId, assets);}}>
                                        <Delete/>
                                      </IconButton>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </Grid>
                        <Grid item xs={12}>
                          <Button color="primary" variant="contained" disabled={this.isSaveDisabled(propsVpp)}
                                  onClick={()=> { this.saveChanges(propsVpp) }}>
                            Save
                          </Button>
                        </Grid>
                      </Grid>
                    );
                  }

                  // Fallthrough
                  return (
                    <div>
                      <Typography>Loading...</Typography>
                      <LoadingSpinner/>
                    </div>
                  );
                }}
              />
            </Paper>      
          </Grid>
        </Grid>
      );
    } else {
      return (
        <Redirect to='/home' />
      );
    }
  }
}
 
ProgramSettingsStage.propTypes = {
  classes: PropTypes.object.isRequired,
};
 
export default withStyles(styleSheet)(ProgramSettingsStage);
